import { Layers, Package, TrendingUp, Receipt, Gift, Lightbulb, CheckCircle2 } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Subscriptions & Billing";

export const tiles: ArticleTile[] = [
  { slug: "plans-basic-vs-standard", title: "Choosing a Plan: Basic vs Standard", category: CATEGORY, description: "Compare features and pick the right plan", icon: Layers },
  { slug: "managing-addons", title: "Adding & Managing Add-ons", category: CATEGORY, description: "Catering, Assets and other optional modules", icon: Package },
  { slug: "prorated-upgrades", title: "Upgrading Mid-Year (Prorated Pricing)", category: CATEGORY, description: "How partial-year upgrades are calculated", icon: TrendingUp },
  { slug: "invoices-billing-history", title: "Viewing Invoices & Billing History", category: CATEGORY, description: "Download PDF invoices for your records", icon: Receipt },
  { slug: "referrals-free-months", title: "Referrals: Earn Free Months", category: CATEGORY, description: "Invite other PTAs and get rewarded", icon: Gift },
];

export const articles: Record<string, ArticleContent> = {
  "plans-basic-vs-standard": {
    title: "Choosing a Plan: Basic vs Standard",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>PTA Hub offers two main plans. Basic covers everyday committee work; Standard unlocks advanced event management and ticketing.</Hero>
        <Section icon={CheckCircle2} title="Basic plan includes">
          <Bullets items={[
            "Treasurer dashboard with income, expenses and reports.",
            "Member management with roles and deputies.",
            "Fundraising page and parent portal.",
            "Marketplace and shop.",
          ]} />
        </Section>
        <Section icon={CheckCircle2} title="Standard plan adds">
          <Bullets items={[
            "Event ticketing and check-in.",
            "Event planner with task assignment.",
            "Stall applications and assignments.",
            "Volunteer signup management.",
          ]} />
        </Section>
        <Tip>Add-ons like Catering and Assets are sold separately and can be added to either plan.</Tip>
      </div>
    ),
  },
  "managing-addons": {
    title: "Adding & Managing Add-ons",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Add-ons unlock extra dashboards and roles for specific committee functions like Catering or Assets.</Hero>
        <Section icon={Package} title="Add an add-on">
          <Steps items={[
            <>Go to <strong>Settings → Subscription</strong>.</>,
            <>Scroll to <strong>Add-ons</strong> and click the one you want.</>,
            <>Complete the secure checkout.</>,
            <>The new dashboard and matching role become available immediately.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="What changes when you remove an add-on">
          <Bullets items={[
            "The matching dashboard hides from the sidebar.",
            "Members assigned to that role keep the data, but the role is hidden until you re-add it.",
            "Existing data is never deleted — re-add the addon to restore visibility.",
          ]} />
        </Section>
      </div>
    ),
  },
  "prorated-upgrades": {
    title: "Upgrading Mid-Year (Prorated Pricing)",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Upgrading from Basic to Standard mid-year only charges you for the remaining months — never the full year.</Hero>
        <Section icon={TrendingUp} title="How prorated pricing works">
          <Bullets items={[
            "We calculate the daily price difference between your current plan and Standard.",
            "You're charged that difference for the days remaining in your subscription.",
            "Your renewal date stays the same, so future renewals stay aligned.",
          ]} />
        </Section>
        <Section icon={Lightbulb} title="How to upgrade">
          <Steps items={[
            <>Open <strong>Settings → Subscription</strong>.</>,
            <>Find the <strong>Standard</strong> plan card.</>,
            <>Click <strong>Upgrade Now</strong> — the prorated amount is shown before checkout.</>,
            <>Complete payment; access updates instantly.</>,
          ]} />
        </Section>
      </div>
    ),
  },
  "invoices-billing-history": {
    title: "Viewing Invoices & Billing History",
    category: CATEGORY,
    readTime: "2 min read",
    content: (
      <div className="space-y-6">
        <Hero>Every subscription payment, plan upgrade and add-on purchase generates a downloadable PDF invoice for your records.</Hero>
        <Section icon={Receipt} title="Find your invoices">
          <Steps items={[
            <>Go to <strong>Settings → Subscription</strong>.</>,
            <>Scroll to <strong>Billing History</strong>.</>,
            <>Click any row to download the PDF invoice.</>,
          ]} />
        </Section>
        <Tip>Invoices include your PTA name, billing address and VAT details where applicable.</Tip>
      </div>
    ),
  },
  "referrals-free-months": {
    title: "Referrals: Earn Free Months",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Refer another PTA to PTA Hub and earn one free month for each PTA that subscribes — up to 6 free months per year.</Hero>
        <Section icon={Gift} title="How it works">
          <Steps items={[
            <>Go to <strong>Settings → Referrals</strong>.</>,
            <>Copy your unique referral link.</>,
            <>Share it with another PTA committee.</>,
            <>When they subscribe, one month is automatically credited to your account.</>,
          ]} />
        </Section>
        <Tip>You can earn up to 6 free months per 12-month period — credits are applied automatically at renewal.</Tip>
      </div>
    ),
  },
};
